import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Application
          </Link>
          
          <div className="flex items-center mb-4">
            <Lock className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
          
          <p className="text-gray-600">
            Last Updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="prose max-w-none">
            
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">1. Information We Collect</h2>
            <p className="text-gray-700 mb-2">We collect the following types of information:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Personal Information:</strong> Name, address, phone number, email address, date of birth</li>
              <li><strong>Financial Information:</strong> Income details, employment information, loan amount requested</li>
              <li><strong>Technical Information:</strong> IP address, browser type, device information, usage data</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-700 mb-2">We use your information to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Connect you with appropriate lending partners</li>
              <li>Verify your identity and eligibility</li>
              <li>Communicate with you about loan options</li>
              <li>Improve our services</li>
              <li>Comply with legal requirements</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">3. Information Sharing</h2>
            <p className="text-gray-700 mb-4">
              We share your information with our network of lending partners who may be able to provide you with loan products. These partners are required to maintain the confidentiality of your information and use it only for legitimate business purposes.
            </p>
            <p className="text-gray-700 mb-2">We may also share information with:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Service providers who help us operate our platform</li>
              <li>Law enforcement when required by law</li>
              <li>Other parties with your explicit consent</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">4. Data Security</h2>
            <p className="text-gray-700 mb-4">
              We employ industry-standard security measures to protect your information, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>256-bit SSL encryption for data transmission</li>
              <li>Secure data storage with access controls</li>
              <li>Regular security audits and updates</li>
              <li>Employee training on data protection</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">5. Your Rights and Choices</h2>
            <p className="text-gray-700 mb-2">You have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Access the personal information we have about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your information (subject to legal requirements)</li>
              <li>Opt-out of marketing communications</li>
              <li>Withdraw consent for information sharing</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">6. Cookies and Tracking</h2>
            <p className="text-gray-700 mb-4">
              We use cookies and similar technologies to improve your experience on our website. These help us understand how you use our service and remember your preferences.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">7. Third-Party Links</h2>
            <p className="text-gray-700 mb-4">
              Our service may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">8. Children's Privacy</h2>
            <p className="text-gray-700 mb-4">
              Our service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children under 18.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">9. State-Specific Rights</h2>
            <p className="text-gray-700 mb-4">
              Residents of certain states (including California, Nevada, and others) may have additional privacy rights under state law. Please contact us for information about exercising these rights.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">10. Data Retention</h2>
            <p className="text-gray-700 mb-4">
              We retain your information for as long as necessary to provide our services, comply with legal obligations, and resolve disputes. Personal information may be retained for up to 7 years or as required by applicable law.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page with an updated revision date.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">12. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700">
                Email: <a href="mailto:privacy@lendsnap.com" className="text-blue-600 hover:text-blue-800">privacy@lendsnap.com</a><br />
                Address: [Your Business Address]<br />
                Phone: [Your Business Phone]
              </p>
            </div>

            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Your Privacy Matters</h3>
              <p className="text-blue-800 text-sm">
                We are committed to protecting your privacy and handling your personal information responsibly. 
                If you have any concerns about how we handle your data, please don't hesitate to contact us.
              </p>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
          >
            Return to Application
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 